import { datadogRum } from '@datadog/browser-rum';
import useSWR from 'swr';
import { getCrossLicensedLocale } from '@/common/utils/license';
import { identityApi } from '$api-clients';
import { useCreateOfferContext } from '../context/CreateOfferContext';
import type { ClientGroup } from '../types';

async function getMemberLicenseState(userId: string, teamId: string) {
  const { data, error } = await identityApi.GET(
    '/teams/{teamId}/members/{userId}',
    {
      params: {
        path: { teamId, userId },
      },
    },
  );
  if (error) {
    throw new Error(`Error fetching license state for ${userId}:${teamId}`, {
      cause: error,
    });
  }
  return data.license?.state;
}

async function fetchBuyerStateOverrides(
  owner: string,
  teamId: string,
  contacts: ClientGroup['contacts'],
): Promise<string[]> {
  const primaryAgentState = await getMemberLicenseState(owner, teamId);
  if (!primaryAgentState) {
    throw new Error('Primary agent license state not found');
  }

  const crossLicensedLocale = getCrossLicensedLocale(primaryAgentState);
  if (!crossLicensedLocale) {
    return [primaryAgentState];
  }

  const otherAgentStates = await Promise.all(
    contacts
      .filter((contact) => contact.type === 'agent' && contact.order > 1)
      .map(async ({ userId, teamId }) => {
        if (!userId || !teamId) {
          return undefined;
        }
        return getMemberLicenseState(userId, teamId);
      }),
  );
  const allStates = [primaryAgentState, ...otherAgentStates].filter(
    (state) => !!state,
  );
  const uniqueStates = Array.from(new Set(allStates)).sort();
  const allStatesAreInCrossLicensedLocale = uniqueStates.every((state) =>
    crossLicensedLocale.includes(state),
  );

  return allStatesAreInCrossLicensedLocale ? uniqueStates : [primaryAgentState];
}

export function useBuyerStateOverrides() {
  const { defaultBuyer } = useCreateOfferContext();
  const owner = defaultBuyer?.owner;
  const teamId = defaultBuyer?.teamId;
  const contacts = defaultBuyer?.contacts;

  const { data } = useSWR(
    owner && teamId
      ? ['buyer-state-overrides', owner, teamId, contacts?.length ?? 0]
      : null,
    ([, ownerId, ownerTeamId]) =>
      fetchBuyerStateOverrides(ownerId, ownerTeamId, contacts ?? []),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      onError: (error) =>
        datadogRum.addError(error, {
          message: 'Error fetching buyer state overrides',
          defaultBuyer,
        }),
    },
  );

  return data;
}
